import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useT, DISCLOSURE_VERSION } from '../lib/i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRODUCT_OPTIONS = [
  { slug: 'auto', labelKey: 'product_auto' },
  { slug: 'home', labelKey: 'product_home' },
  { slug: 'bundle', labelKey: 'product_bundle' },
  { slug: 'renters', labelKey: 'product_renters' },
  { slug: 'business', labelKey: 'product_business' },
  { slug: 'life', labelKey: 'product_life' },
];

const PRODUCT_TO_COVERAGE = {
  auto: 'auto', home: 'home', bundle: 'auto',
  renters: 'renters', business: 'business', life: 'life',
};

const BEST_TIMES_EN = ['Morning (8am – 12pm)', 'Afternoon (12pm – 5pm)', 'Evening (5pm – 8pm)', 'Anytime'];
const BEST_TIMES_ES = ['Mañana (8am – 12pm)', 'Tarde (12pm – 5pm)', 'Noche (5pm – 8pm)', 'Cualquier hora'];

const PHONE_RE = /^[+()\-\s\d]{7,}$/;

const CallbackRequest = () => {
  const [params] = useSearchParams();
  const [lang, t, setLang] = useT(params.get('lang'));

  const [data, setData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    product: '',
    best_time_to_contact: '',
    website_url: '',
  });
  const [consentContact, setConsentContact] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [errs, setErrs] = useState({});

  const update = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setErrs((e) => ({ ...e, [k]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const fe = {};
    if (!data.first_name.trim()) fe.first_name = t('err_required');
    if (!data.phone.trim()) fe.phone = t('err_required');
    else if (!PHONE_RE.test(data.phone)) fe.phone = t('err_phone_invalid');
    if (!data.product) fe.product = t('err_required');
    setErrs(fe);
    if (!consentContact) { setError(t('err_consent_required')); return; }
    if (Object.keys(fe).length) return;

    setSubmitting(true);
    try {
      const coverage_type = PRODUCT_TO_COVERAGE[data.product] || 'auto';
      const payload = {
        coverage_type,
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        phone: data.phone.trim(),
        best_time_to_contact: data.best_time_to_contact,
        notes: data.product === 'bundle' ? '[Callback request — Bundle: Home + Auto]' : '[Callback request]',
        consent: true,
        source: 'Website',
        language: lang,
        request_type: 'callback',
        campaign_product: data.product,
        preferred_contact_method: 'phone',
        consent_record: {
          contact: true,
          marketing_email: false,
          marketing_sms: false,
          disclosure_version: DISCLOSURE_VERSION,
          language: lang,
          submitted_at: new Date().toISOString(),
        },
        utm: {
          source: params.get('utm_source') || '',
          medium: params.get('utm_medium') || '',
          campaign: params.get('utm_campaign') || '',
          content: params.get('utm_content') || '',
          term: params.get('utm_term') || '',
        },
        website_url: data.website_url,
      };
      const res = await axios.post(`${API}/leads`, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 20000,
      });
      if (!res?.data?.ok) throw new Error('Submission did not confirm success.');
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Callback request failed:', err);
      if (err?.response?.status === 429) setError(t('err_rate_limit'));
      else setError(t('err_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const times = lang === 'es' ? BEST_TIMES_ES : BEST_TIMES_EN;

  if (done) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-2xl" data-testid="callback-confirmation">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-lg text-center">
          <div className="text-5xl mb-4">📞</div>
          <h1 className="text-3xl font-bold mb-3" style={{ color: '#01325B' }}>{t('thank_title')}</h1>
          <p className="text-gray-700 mb-6">{t('callback_thank')}</p>
          <Link to={`/quote?lang=${lang}`} className="inline-block px-6 py-3 rounded-lg font-semibold text-white" style={{ background: '#E07A2C' }} data-testid="callback-back">
            {t('thank_back')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="callback-page">
      {/* Lang toggle */}
      <div className="bg-[#01325B] text-white">
        <div className="container mx-auto px-4 py-2 flex items-center justify-end gap-3 text-sm">
          <button type="button" onClick={() => setLang('en')} data-testid="lang-toggle-en" className={`px-3 py-1 rounded ${lang === 'en' ? 'bg-[#E07A2C] font-semibold' : 'hover:underline'}`} aria-pressed={lang === 'en'}>{t('lang_english')}</button>
          <span aria-hidden="true" className="opacity-60">|</span>
          <button type="button" onClick={() => setLang('es')} data-testid="lang-toggle-es" className={`px-3 py-1 rounded ${lang === 'es' ? 'bg-[#E07A2C] font-semibold' : 'hover:underline'}`} aria-pressed={lang === 'es'}>{t('lang_spanish')}</button>
        </div>
      </div>

      <section className="py-12 bg-[#f6f5f1]">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="text-center mb-6">
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: '#01325B' }}>{t('callback_title')}</h1>
            <p className="text-gray-600 text-sm">{t('callback_sub')}</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 md:p-10 space-y-4" data-testid="callback-form" noValidate>
            {/* Honeypot */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}>
              <input type="text" tabIndex={-1} autoComplete="off" value={data.website_url} onChange={(e) => update('website_url', e.target.value)} data-testid="honeypot-field" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#01325B' }}>{t('f_first_name')} *</label>
                <input className="input" value={data.first_name} onChange={(e) => update('first_name', e.target.value)} data-testid="input-first-name" />
                {errs.first_name && <p className="text-sm text-red-600 mt-1">{errs.first_name}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#01325B' }}>{t('f_last_name')}</label>
                <input className="input" value={data.last_name} onChange={(e) => update('last_name', e.target.value)} data-testid="input-last-name" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: '#01325B' }}>{t('f_phone')} *</label>
              <input type="tel" className="input" value={data.phone} onChange={(e) => update('phone', e.target.value)} data-testid="input-phone" />
              {errs.phone && <p className="text-sm text-red-600 mt-1">{errs.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: '#01325B' }}>{t('f_product')} *</label>
              <select className="input" value={data.product} onChange={(e) => update('product', e.target.value)} data-testid="input-product">
                <option value="">{t('f_product_placeholder')}</option>
                {PRODUCT_OPTIONS.map((p) => (<option key={p.slug} value={p.slug}>{t(p.labelKey)}</option>))}
              </select>
              {errs.product && <p className="text-sm text-red-600 mt-1">{errs.product}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: '#01325B' }}>{t('f_best_time')}</label>
              <select className="input" value={data.best_time_to_contact} onChange={(e) => update('best_time_to_contact', e.target.value)} data-testid="input-best-time">
                <option value="">—</option>
                {times.map((v, i) => <option key={v} value={BEST_TIMES_EN[i]}>{v}</option>)}
              </select>
            </div>

            <label className="flex items-start gap-3 cursor-pointer bg-[#f6f5f1] p-3 rounded-lg">
              <input type="checkbox" className="mt-1" checked={consentContact} onChange={(e) => { setConsentContact(e.target.checked); if (e.target.checked) setError(''); }} data-testid="consent-contact" />
              <span className="text-sm text-gray-800">
                {t('consent_required_label')}
                <span className="block text-xs text-gray-500 italic">{t('consent_required_help')}</span>
              </span>
            </label>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm" data-testid="submit-error">{error}</div>
            )}

            <button type="submit" disabled={submitting} data-testid="submit-btn" className="w-full px-6 py-4 rounded-lg font-semibold text-white disabled:opacity-60" style={{ background: '#E07A2C' }}>
              {submitting ? t('f_submitting') : t('callback_submit')}
            </button>
          </form>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          padding: 0.65rem 0.85rem;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          font-size: 1rem;
          background: white;
          color: #111;
        }
        .input:focus { outline: 2px solid #E07A2C; outline-offset: 1px; border-color: #E07A2C; }
      `}</style>
    </div>
  );
};

export default CallbackRequest;
